import { useRef, useState } from "react";
import {
  generateRouteModesContentStream,
  generateRouteModesDirectionsStream,
  generateRouteModesImagesStream,
  generateRouteModesParkingRequest,
} from "@services/ai/aiApi";
import {
  buildRoutePointAutomationPayloadForAction,
  getAutomationPointResults,
  isDirectionPoint,
  isMainDrivingRoutePoint,
  mergeAutomatedRoutePoints,
} from "@utils/mode_tool/routePointAutomation";

const ACTIONS = {
  content: {
    title: "AI automate points",
  },
  directions: {
    title: "AI direction points",
  },
  parking: {
    title: "AI parking point",
    request: generateRouteModesParkingRequest,
  },
  images: {
    title: "AI generate images",
  },
};

function useRoutePointAutomation({
  tourId,
  routeType,
  routeRaw,
  tourName,
  routeName,
  modePoints,
  onApplyPoints,
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [results, setResults] = useState({ succeeded: [], failed: [], created: [] });
  const [activeAction, setActiveAction] = useState("content");
  const [imagePointScope, setImagePointScope] = useState("regular");
  const [targetImagePoint, setTargetImagePoint] = useState(null);
  const [streamProgress, setStreamProgress] = useState({
    total: 0,
    completed: 0,
    points: [],
    done: false,
    completeReceived: false,
  });
  const streamedContentRowsRef = useRef([]);
  const processedDirectionPointIdsRef = useRef(new Set());
  const streamedDirectionRowsRef = useRef([]);
  const streamedImageRowsRef = useRef([]);

  function openForAction(action = "content", imagePoint = null) {
    if (!ACTIONS[action] || isGenerating) return;
    setActiveAction(action);
    setTargetImagePoint(imagePoint);
    setError("");
    setSuccess("");
    setResults({ succeeded: [], failed: [], created: [] });
    setStreamProgress({ total: 0, completed: 0, points: [], done: false, completeReceived: false });
    setIsOpen(true);
  }

  async function generate(action = activeAction) {
    const actionConfig = ACTIONS[action];
    if (!actionConfig) return;
    setActiveAction(action);
    setError("");
    setSuccess("");
    setResults({ succeeded: [], failed: [], created: [] });
    setStreamProgress({ total: 0, completed: 0, points: [], done: false, completeReceived: false });
    const type = String(routeType || "").trim().toLowerCase();
    const points = Array.isArray(modePoints) ? modePoints : [];
    if (!["walking", "driving"].includes(type)) {
      setError("AI point automation is only available for Walking and Driving routes.");
      setIsOpen(true);
      return;
    }
    if (!points.length || points.length > 500) {
      setError("Select a route with between 1 and 500 points.");
      setIsOpen(true);
      return;
    }
    if (action === "parking" && type !== "driving") {
      setError("AI parking points are only available for Driving routes.");
      setIsOpen(true);
      return;
    }
    const contentPoints = action === "content"
      ? points.filter((point) => !isDirectionPoint(point))
      : [];
    const parkingPoints = action === "parking"
      ? points.filter((point, index) => isMainDrivingRoutePoint(point, index))
      : [];
    const directionPoints = action === "directions"
      ? points.filter((point) =>
          isDirectionPoint(point) &&
          !processedDirectionPointIdsRef.current.has(String(point?.id || "")),
        )
      : [];
    const imageSourcePoints = targetImagePoint ? [targetImagePoint] : points;
    const imagePoints = action === "images"
      ? imageSourcePoints.filter((point) => targetImagePoint || imagePointScope === "all"
        || (imagePointScope === "directions"
          ? isDirectionPoint(point)
          : !isDirectionPoint(point)))
      : [];
    const expectedDirectionIds = new Set(
      directionPoints.map((point) => String(point?.id || "")),
    );
    // Image generation sends only the point subset selected in the modal.
    const requestPoints = action === "parking"
      ? parkingPoints
      : action === "images" ? imagePoints : points;
    if (
      (action === "content" && !contentPoints.length) ||
      (action === "directions" && !directionPoints.length) ||
      (action === "images" && !imagePoints.length) ||
      !requestPoints.length
    ) {
      setError(action === "content"
        ? "There are no regular points to generate content for."
          : action === "directions"
            ? "There are no Direction points to regenerate."
            : action === "images"
              ? imagePointScope === "directions"
                ? "There are no Direction points to generate images for."
                : imagePointScope === "all"
                  ? "There are no route points to generate images for."
                  : "There are no regular route points to generate images for."
              : "There are no main route points to check for parking.");
      setIsOpen(true);
      return;
    }
    if (action === "images" && imagePoints.some((point) => !String(point?.id || "").trim())) {
      setError("Each route point needs an ID before generating images.");
      setIsOpen(true);
      return;
    }
    if (action === "content" || action === "directions") {
      if (!routeRaw || typeof routeRaw !== "object") {
        setError("Route geometry is required. Wait for the route to finish loading and try again.");
        setIsOpen(true);
        return;
      }
      const invalidPoint = requestPoints.find((point) => {
        const latitude = point?.latitude ?? point?.lat;
        const longitude = point?.longitude ?? point?.lng;
        return (
          !String(point?.id || "").trim() ||
          latitude === null || latitude === undefined || String(latitude).trim() === "" ||
          longitude === null || longitude === undefined || String(longitude).trim() === "" ||
          !Number.isFinite(Number(latitude)) ||
          !Number.isFinite(Number(longitude))
        );
      });
      if (invalidPoint) {
        setError("Each route point needs an ID and valid latitude and longitude.");
        setIsOpen(true);
        return;
      }
    }
    if (!String(tourId || "").trim()) {
      setError("The tour ID is unavailable. Select a tour before generating points.");
      setIsOpen(true);
      return;
    }
    if (typeof onApplyPoints !== "function") {
      setError("Generated route points cannot be applied right now.");
      setIsOpen(true);
      return;
    }

    setIsOpen(true);
    setIsGenerating(true);
    if (["content", "directions", "images"].includes(action)) {
      const isDirectionAction = action === "directions";
      const streamedRowsRef = isDirectionAction
        ? streamedDirectionRowsRef
        : action === "images" ? streamedImageRowsRef : streamedContentRowsRef;
      streamedRowsRef.current = [];
      setStreamProgress({
        total: isDirectionAction ? directionPoints.length : action === "images" ? imagePoints.length : contentPoints.length,
        completed: 0,
        done: false,
        completeReceived: false,
        points: (isDirectionAction ? directionPoints : action === "images" ? imagePoints : contentPoints).map((point, index) => ({
          index: points.indexOf(point),
          id: String(point?.id || `point-${index + 1}`),
          point,
          status: "pending",
        })),
      });
    }
    try {
      const payload = buildRoutePointAutomationPayloadForAction({
        tourId,
        routeType: type,
        routeRaw,
        points: requestPoints,
        action,
        tourName,
        routeName,
      });
      const streamRequest = action === "content"
        ? generateRouteModesContentStream
        : action === "directions"
          ? generateRouteModesDirectionsStream
          : action === "images" ? generateRouteModesImagesStream : null;
      const response = streamRequest
        ? await streamRequest(payload, async ({ event, data }) => {
            if (event === "start") {
              setStreamProgress((current) => ({
                ...current,
                total: action === "directions"
                  ? directionPoints.length
                  : action === "images" ? imagePoints.length : current.total,
              }));
            }
            if (event === "point") {
              const isDirectionAction = action === "directions";
              const isImageAction = action === "images";
              const streamedRowsRef = isDirectionAction
                ? streamedDirectionRowsRef
                : isImageAction ? streamedImageRowsRef : streamedContentRowsRef;
              const processedIdsRef = processedDirectionPointIdsRef;
              const streamedPoint = data?.point;
              if (!isDirectionAction && !isImageAction && streamedPoint && isDirectionPoint(streamedPoint)) {
                return;
              }
              const pointId = String(data?.id || streamedPoint?.id || "");
              if (
                isDirectionAction &&
                (!expectedDirectionIds.has(pointId) ||
                  (streamedPoint && !isDirectionPoint(streamedPoint)))
              ) return;
              if (isImageAction && !imagePoints.some((point) => String(point?.id || "") === pointId)) return;
              const pointIndex = Number.isInteger(Number(data?.index)) && Number(data?.index) >= 0
                ? Number(data.index)
                : streamedRowsRef.current.length;
              if (streamedPoint) {
                streamedRowsRef.current[pointIndex] = streamedPoint;
                const streamedRows = streamedRowsRef.current.filter(Boolean);
                const partiallyUpdatedPoints = mergeAutomatedRoutePoints(
                  points,
                  streamedRows,
                  type,
                );
                await onApplyPoints(partiallyUpdatedPoints, {
                  routeRecalculationRequired: false,
                  hasNewPoints: false,
                });
                const processedId = String(data?.id || streamedPoint?.id || "");
                if (isDirectionAction && processedId) processedIdsRef.current.add(processedId);
              }
              setStreamProgress((current) => {
                const nextPoints = [...current.points];
                const progressIndex = nextPoints.findIndex((item) => item.id === pointId);
                const targetIndex = progressIndex >= 0 ? progressIndex : pointIndex;
                if (targetIndex >= nextPoints.length) return current;
                nextPoints[targetIndex] = {
                  index: targetIndex,
                  id: pointId || nextPoints[targetIndex]?.id || `point-${targetIndex + 1}`,
                  point: data?.point || nextPoints[targetIndex]?.point || null,
                  status: String(data?.status || "completed"),
                  message: String(data?.message || data?.point?.error_message || ""),
                };
                return {
                  ...current,
                  points: nextPoints,
                  completed: nextPoints.filter((item) => item.status !== "pending").length,
                  total: current.total,
                };
              });
            }
            if (event === "complete") {
              setStreamProgress((current) => ({
                ...current,
                completed: current.total || current.completed,
                done: true,
                completeReceived: true,
              }));
            }
          })
        : await actionConfig.request(payload);
      if (response?.error || response?.success === false) {
        throw new Error(response?.message || `Could not run ${actionConfig.title}.`);
      }
      const body = response?.body ?? response?.data ?? response;
      const returnedPoints = Array.isArray(body?.points) ? body.points : null;
      if (!returnedPoints) throw new Error("The AI response does not contain a points list.");
      if (action === "parking") {
        setStreamProgress({
          total: returnedPoints.length,
          completed: returnedPoints.length,
          points: [],
          done: true,
          completeReceived: true,
        });
      }
      const responsePoints = action === "content"
        ? returnedPoints.filter((point) => !isDirectionPoint(point))
        : action === "directions"
          ? returnedPoints.filter((point) =>
              isDirectionPoint(point) &&
              expectedDirectionIds.has(String(point?.id || "")),
            )
          : action === "images"
            ? returnedPoints.filter((point) =>
                imagePoints.some((source) => String(source?.id || "") === String(point?.id || "")),
              )
            : returnedPoints;
      if (["content", "directions", "images"].includes(action)) {
        setStreamProgress((current) => ({
          ...current,
          total: current.total || responsePoints.length,
          completed: responsePoints.length,
          done: true,
          completeReceived: true,
          points: responsePoints.map((point, index) => {
            const streamed = current.points.find((item) => item.id === String(point?.id || ""));
            return {
              index,
              id: String(point?.id || streamed?.id || `point-${index + 1}`),
              point,
              status: streamed?.status && streamed.status !== "pending"
                ? streamed.status
                : point?.error === true
                  ? "error"
                  : "completed",
              message: streamed?.message || String(point?.error_message || point?.message || ""),
            };
          }),
        }));
      }
      const updatedPoints = mergeAutomatedRoutePoints(points, responsePoints, type);
      const resultSourcePoints = action === "directions"
        ? directionPoints
        : action === "images" ? imagePoints : requestPoints;
      const nextResults = getAutomationPointResults(
        resultSourcePoints,
        responsePoints,
        updatedPoints,
      );
      setResults(nextResults);
      await onApplyPoints(updatedPoints, {
        routeRecalculationRequired: Boolean(body?.route_recalculation_required),
        hasNewPoints: responsePoints.some((point) => point?.new_point === true),
      });
      setStreamProgress((current) => ({ ...current, done: true, completeReceived: true }));
      const failedCount = nextResults.failed.length;
      const completedCount = nextResults.succeeded.length;
      setSuccess(
        action === "images"
          ? `${completedCount} image${completedCount === 1 ? "" : "s"} generated${failedCount ? `; ${failedCount} failed` : ""}. Review the route and save your changes.`
          : action === "parking" && !nextResults.created.length && !failedCount
          ? "No suitable parking point was found."
          : `${completedCount} points completed${nextResults.created.length ? `; ${nextResults.created.length} new point(s) added` : ""}${failedCount ? `; ${failedCount} failed` : ""}. Review the route and save your changes.`,
      );
    } catch (requestError) {
      setError(requestError?.message || `Could not run ${actionConfig.title}.`);
      if (["content", "directions", "images"].includes(action)) {
        setStreamProgress((current) => ({ ...current, done: true }));
      }
    } finally {
      setIsGenerating(false);
    }
  }

  function close() {
    if (!isGenerating) setIsOpen(false);
  }

  return {
    isOpen,
    isGenerating,
    activeAction,
    imagePointScope,
    setImagePointScope,
    streamProgress,
    error,
    success,
    results,
    generate,
    openContent: () => openForAction("content"),
    openDirections: () => openForAction("directions"),
    openParking: () => openForAction("parking"),
    openImages: () => openForAction("images"),
    openImageForPoint: (point) => openForAction("images", point),
    isSingleImagePoint: Boolean(targetImagePoint),
    generateContent: () => generate("content"),
    generateDirections: () => generate("directions"),
    generateParking: () => generate("parking"),
    generateImages: () => generate("images"),
    close,
  };
}

export default useRoutePointAutomation;
